import { EventChannel, SagaIterator } from 'redux-saga'
import { put, takeLatest, select, fork, call, take, all, takeEvery } from 'redux-saga/effects'

import JitsiMeetJS from 'lib-jitsi-meet'

import * as navbarActions from '@tabeeb/modules/sessionTabs/actions'
import * as conferenceActions from '@tabeeb/modules/presentation/actions/conference'
import * as connectionActions from '@tabeeb/modules/presentation/actions/connection'
import * as jitsiTrackActions from '@tabeeb/modules/presentation/actions/jitsiTrack'
import * as devicesActions from '@tabeeb/modules/presentation/actions/devices'
import * as tracksActions from '@tabeeb/modules/presentation/actions/tracks'
import * as rawContentActions from '@tabeeb/shared/content/actions'
import * as contentStateSelectors from '@tabeeb/shared/content/selectors'
import bindEvent from '@tabeeb/modules/presentation/services/events'
import { notificationActions } from '@tabeeb/modules/notification'
import { signalrActions, signalrEvents } from '@tabeeb/modules/signalr'
import { TabPanel, TabPanelLocation } from '@tabeeb/enums'
import * as accountSelectors from '@tabeeb/modules/account/selectors'
import * as whiteboardService from '@tabeeb/modules/whiteboard/services'
import * as trackService from '@tabeeb/modules/presentation/services/trackService'
import * as connectionService from '@tabeeb/modules/presentation/services/connectionService'
import { usersActions } from '@tabeeb/modules/../users'
import {
  getIsFormsAndUsersTabCollapsed,
  getShowVideobridgeRegionDropdown,
} from '@tabeeb/modules/appConfigState/selectors'
import { tabeebHubName } from '@tabeeb/modules/signalr/services/constants'
import { getNavbarCurrentBottomPanel } from '@tabeeb/modules/sessionTabs/selectors'

import {
  getIsCallStarted,
  getJitsiAccessToken,
  getJitsiConference,
  getJitsiConnection,
  getJitsiRoomId,
} from '../selectors'

let externalEventChannels: EventChannel<unknown>[] = []

function* startCall(): SagaIterator {
  const currentBottomPanel: ReturnType<typeof getNavbarCurrentBottomPanel> = yield select(getNavbarCurrentBottomPanel)

  const isFormsAndUsersTabCollapsed: ReturnType<typeof getIsFormsAndUsersTabCollapsed> = yield select(
    getIsFormsAndUsersTabCollapsed
  )

  if (isFormsAndUsersTabCollapsed && currentBottomPanel?.name !== TabPanel.Users) {
    // TODO: Fix the ts-ignore by typing the navbarActions properly
    // @ts-ignore
    yield put(navbarActions.switchPanel({ name: TabPanel.Users }, TabPanelLocation.Bottom))
  }
}

function* notifyStartCall(): SagaIterator {
  const contentId: ReturnType<typeof contentStateSelectors.getContentId> = yield select(
    contentStateSelectors.getContentId
  )
  const presenterId: ReturnType<typeof contentStateSelectors.getPresenterId> = yield select(
    contentStateSelectors.getPresenterId
  )

  const roomId = `${contentId}`

  yield call(whiteboardService.enterRoom, roomId, presenterId)
}

function* onUserReadyForCall(): SagaIterator {
  const jitsiRoom: ReturnType<typeof getJitsiConference> = yield select(getJitsiConference)

  console.log('Jitsi Room object:', jitsiRoom)
  jitsiRoom?.join()

  const contentId: ReturnType<typeof contentStateSelectors.getContentId> = yield select(
    contentStateSelectors.getContentId
  )
  const presenterId: ReturnType<typeof contentStateSelectors.getPresenterId> = yield select(
    contentStateSelectors.getPresenterId
  )
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  yield put(conferenceActions.closeCallPreviewDialog())

  if (currentUserId === presenterId) {
    yield put(rawContentActions.setPresenterRequest({ contentId, presenterId }))
    yield take([rawContentActions.setPresenterSuccess])

    yield put(signalrActions.invokeHubAction({ method: 'SetPresenter', args: [contentId, presenterId] }))
  }
  yield put(signalrActions.invokeHubAction({ method: 'SubscribeToConferenceEvents', args: [contentId] }))

  yield put(
    jitsiTrackActions.attachAudio({
      userId: currentUserId,
      container: trackService.getAudioContainerById(currentUserId),
    })
  )
  yield put(jitsiTrackActions.attachVideo(currentUserId))
}

function* disconnect(): SagaIterator {
  yield put(connectionActions.dispose())
  yield put(connectionActions.endCall())

  const contentId: ReturnType<typeof contentStateSelectors.getContentId> = yield select(
    contentStateSelectors.getContentId
  )

  const isCurrentUserPresenter: ReturnType<typeof contentStateSelectors.getIsCurrentUserPresenter> = yield select(
    contentStateSelectors.getIsCurrentUserPresenter
  )
  if (isCurrentUserPresenter) {
    yield put(rawContentActions.setPresenterRequest({ contentId, presenterId: null }))
    yield take([rawContentActions.setPresenterSuccess])

    yield put(signalrActions.invokeHubAction({ method: 'SetPresenter', args: [contentId, null] }))
    yield put(rawContentActions.setLastCallEndTime(new Date()))
  }
}

function* enterRoom(action: ReturnType<typeof connectionActions.enterRoom>): SagaIterator {
  window.config.nick = yield select(accountSelectors.getCurrentUserId)

  yield put(connectionActions.setRoomId(action.payload))
  yield put(connectionActions.initJitsi())
}

function* initJitsi(): SagaIterator {
  const roomId = yield select(contentStateSelectors.getContentId)

  const currentVideoBridge = yield select((state) => state.whiteboard.currentVideoBridge)
  console.log('Current video bridge:', currentVideoBridge)

  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  window.config.nick = `${currentUserId}`
  if (window.config.preferBosh || !window.config.websocket) {
    window.config.serviceUrl = `${window.config.bosh}${roomId}`
  } else {
    window.config.serviceUrl = `${window.config.websocket}${roomId}`
  }

  window.config.disableBeforeUnloadHandlers = true

  const isManualVideoBridgeSelection = yield select(getShowVideobridgeRegionDropdown)
  if (isManualVideoBridgeSelection) {
    if (currentVideoBridge?.url) {
      const domain = currentVideoBridge.url

      window.config.hosts.domain = domain
      window.config.hosts.muc = `conference.${domain}`
      window.config.hosts.focus = `focus.${domain}`
      window.config.serviceUrl = `https://${domain}/http-bind?room=${roomId}`
      window.config.name = 'Tabeeb'
    }
  }
  yield call(connectionService.initJitsi)
  yield put(connectionActions.initJitsiConnection())
}

function* initJitsiConnection(): SagaIterator {
  const accessToken: ReturnType<typeof getJitsiAccessToken> = yield select(getJitsiAccessToken)
  const jitsiConnection = new JitsiMeetJS.JitsiConnection(window.config.appId, accessToken, window.config)
  console.log('Jitsi connection object:', jitsiConnection)

  const actions = []
  actions.push(put(connectionActions.setJitsiConnection(jitsiConnection)))
  actions.push(put(connectionActions.bindConnectionEvents()))

  jitsiConnection.connect()

  yield all(actions)
}

function* onConnectionEstablished() {
  yield put(connectionActions.unbindConnectionEvents())
  yield put(conferenceActions.initRoom())
}

function* onConnectionFailed(action: ReturnType<typeof connectionActions.onConnectionFailed>): SagaIterator {
  console.error('Jitsi CONNECTION_FAILED reason:', action?.payload || action)
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  yield put(connectionActions.unbindConnectionEvents())
  yield put(notificationActions.onAddErrorNotification({ message: 'Failed to connect to call' }))
  yield put(connectionActions.dispose())
  yield put(connectionActions.endCall())
  yield put(connectionActions.setPresenter(null))
  yield put(usersActions.resetVideoLoadForUser(currentUserId))
}

function* bindConnectionEvents(): SagaIterator {
  const jitsiConnection: ReturnType<typeof getJitsiConnection> = yield select(getJitsiConnection)
  if (!jitsiConnection) {
    return
  }

  yield fork(
    bindEvent,
    jitsiConnection,
    JitsiMeetJS.events.connection.CONNECTION_FAILED,
    connectionActions.onConnectionFailed,
    externalEventChannels
  )

  yield fork(
    bindEvent,
    jitsiConnection,
    JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED,
    connectionActions.onConnectionEstablished,
    externalEventChannels
  )
}

function unbindConnectionEvents() {
  externalEventChannels.forEach((channel) => channel.close())
  externalEventChannels = []
}

function* dispose(): SagaIterator {
  yield put(conferenceActions.leaveRoom())
  yield put(tracksActions.stopTracks())

  const connection: ReturnType<typeof getJitsiConnection> = yield select(getJitsiConnection)
  if (connection) {
    connection.disconnect()
  }

  yield put(conferenceActions.disposeConference())
  yield put(tracksActions.disposeTracks())
  yield put(connectionActions.resetJitsiConnection())
  yield put(devicesActions.resetDevices())
}

function* onConnected(): SagaIterator {
  const roomId: ReturnType<typeof getJitsiRoomId> = yield select(getJitsiRoomId)
  const callStarted: ReturnType<typeof getIsCallStarted> = yield select(getIsCallStarted)

  if (roomId > 0 && callStarted) {
    yield put(signalrActions.invokeHubAction({ method: 'SubscribeToConferenceEvents', args: [roomId] }))
  }
}

function* connectionSaga() {
  yield all([
    takeLatest(connectionActions.startCall, startCall),
    takeLatest(connectionActions.notifyStartCall, notifyStartCall),
    takeLatest(connectionActions.onUserReadyForCall, onUserReadyForCall),
    takeLatest(connectionActions.disconnect, disconnect),
    takeLatest(connectionActions.enterRoom, enterRoom),
    takeLatest(connectionActions.initJitsi, initJitsi),
    takeLatest(connectionActions.initJitsiConnection, initJitsiConnection),
    takeLatest(connectionActions.onConnectionEstablished, onConnectionEstablished),
    takeLatest(connectionActions.onConnectionFailed, onConnectionFailed),
    takeLatest(connectionActions.bindConnectionEvents, bindConnectionEvents),
    takeLatest(connectionActions.unbindConnectionEvents, unbindConnectionEvents),
    takeLatest(connectionActions.dispose, dispose),
    takeEvery(signalrEvents[tabeebHubName].onConnected, onConnected),
  ])
}

export default connectionSaga
